import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MoreVertical, Edit2, Trash, Video, Trophy, Target } from 'lucide-react';
import { TrainingSession } from '../types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface SessionCardProps {
  session: TrainingSession;
  onViewDetails: (session: TrainingSession) => void;
  onEdit: (session: TrainingSession) => void;
  onDelete?: (session: TrainingSession) => void;
}

export function SessionCard({ session, onViewDetails, onEdit, onDelete }: SessionCardProps) {
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(session);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(session);
      e.currentTarget.closest('html')?.click();
    }
  };

  const hasTextEntries = session.learned || session.improvements || session.successes || session.notes;
  const isCompetition = session.title === 'Competition' || session.sessionType === 'Competition';
  
  // Calculate losses for competition display
  const totalLosses = isCompetition && session.competitionTotalMatches && session.competitionTotalWins 
    ? session.competitionTotalMatches - session.competitionTotalWins 
    : 0;

  return (
    <Card className="hover:shadow-sm transition-all text-left">
      <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
        <div>
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg font-medium">{session.title}</CardTitle>
            {session.youtubeUrl && (
              <span title="Has video">
                <Video className="h-4 w-4 text-primary" />
              </span>
            )}
          </div>
          <CardDescription>{session.date} · {session.duration} minutes</CardDescription>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleEdit}>
              <Edit2 className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            {onDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem 
                    onSelect={(e: Event) => e.preventDefault()}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Training Session</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this training session? 
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDelete}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Techniques Section */}
        {session.techniques && session.techniques.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {session.techniques.slice(0, 3).map((technique, index) => (
              <Badge key={index} variant="secondary">
                {technique}
              </Badge>
            ))}
            {session.techniques.length > 3 && (
              <Badge variant="outline">
                +{session.techniques.length - 3} more
              </Badge>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No techniques recorded
          </p>
        )}

        {/* Competition Summary - Positioned between techniques and reflections */}
        {isCompetition && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-5 w-5 text-amber-600" />
              <h4 className="font-semibold text-amber-800">Competition Results</h4>
            </div>
            
            {/* Outcome */}
            {session.competitionFinish && (
              <div className="mb-3">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">Outcome:</span>
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                    {session.competitionFinish}
                  </Badge>
                </div>
              </div>
            )}
            
            {/* Match Statistics - All in one row */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-xs text-amber-600 font-medium">Total Matches:</span>
                <span className="text-lg font-bold text-amber-800">{session.competitionTotalMatches || 0}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-xs text-amber-600 font-medium">Wins:</span>
                <span className="text-lg font-bold text-green-600">{session.competitionTotalWins || 0}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-xs text-amber-600 font-medium">Losses:</span>
                <span className="text-lg font-bold text-red-600">{totalLosses}</span>
              </div>
            </div>

            {/* Weight Classes if available */}
            {session.competitionWeightClasses && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <div className="text-xs text-amber-600 font-medium mb-1">Weight Classes</div>
                <div className="text-sm text-amber-800">{session.competitionWeightClasses}</div>
              </div>
            )}
          </div>
        )}
        
        {/* Reflection sections */}
        {hasTextEntries && (
          <div className="space-y-3">
            {session.learned && (
              <div>
                <h4 className="text-sm font-medium">What I Learned:</h4>
                <p className="text-sm text-muted-foreground">{session.learned}</p>
              </div>
            )}
            
            {session.improvements && (
              <div>
                <h4 className="text-sm font-medium">Areas for Improvement:</h4>
                <p className="text-sm text-muted-foreground">{session.improvements}</p>
              </div>
            )}
            
            {session.successes && (
              <div>
                <h4 className="text-sm font-medium">What Went Well:</h4>
                <p className="text-sm text-muted-foreground">{session.successes}</p>
              </div>
            )}
            
            {!session.learned && !session.improvements && !session.successes && session.notes && (
              <div>
                <h4 className="text-sm font-medium">Notes:</h4>
                <p className="text-sm text-muted-foreground">{session.notes}</p>
              </div>
            )}
          </div>
        )}
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onViewDetails(session)}
        >
          View Full Details
        </Button>
      </CardContent>
    </Card>
  );
}
